import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { Mail, MapPin } from "lucide-react";
import { fetcher } from "@/lib/api";
import "@/i18n/fase5";

const OFFICIAL_PARTNERS = [
  {
    src: "/brand/client/eu-nextgen.png",
    alt: "Unión Europea · NextGenerationEU",
    href: "https://next-generation-eu.europa.eu/",
    key: "eu",
  },
  {
    src: "/brand/client/mit-gobes.png",
    alt: "Gobierno de España · Ministerio de Industria y Turismo",
    href: "https://www.mintur.gob.es/",
    key: "mit",
  },
  {
    src: "/brand/client/prtr.png",
    alt: "Plan de Recuperación, Transformación y Resiliencia",
    href: "https://planderecuperacion.gob.es/",
    key: "prtr",
  },
  {
    src: "/brand/client/mancomunidade-alt.png",
    alt: "Mancomunidade do Salnés",
    href: "https://mancomunidadedosalnes.gal/",
    key: "mancomunidade",
  },
  {
    src: "/brand/client/segittur.png",
    alt: "SEGITTUR · Turismo e Innovación",
    href: "https://www.segittur.es/",
    key: "segittur",
  },
  {
    src: "/brand/client/pid.png",
    alt: "Plataforma Inteligente de Destinos",
    href: "https://www.destinosinteligentes.es/",
    key: "pid",
  },
];

export default function PublicFooter() {
  const { t, i18n } = useTranslation();
  const year = new Date().getFullYear();
  const { data: settings } = useSWR("/settings/public", fetcher);
  const isEs = i18n.language?.startsWith("es");
  // Use the admin-configurable financing statement when present. Fallback to
  // the compact i18n copy ("Cofinanciado por…") only if the API hasn't
  // responded yet or the key is empty.
  const financingBody =
    (isEs ? settings?.financing_statement_es : settings?.financing_statement_gl) ||
    t("funding.body");
  return (
    <footer className="bg-salnes-ink text-white/80 border-t border-white/10">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <img
                src="/brand/client/mancomunidade.png"
                alt="Mancomunidade do Salnés"
                className="h-11 w-auto bg-white/95 rounded p-1"
                data-testid="footer-logo-mancomunidade"
              />
              <div>
                <div className="font-serif text-white text-lg leading-tight" data-testid="public-footer-brand-name">
                  {t("brand.name")}
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">
                  {t("brand.tagline")}
                </div>
              </div>
            </div>
            <p className="mt-6 text-sm max-w-md text-white/60 leading-relaxed" data-testid="footer-financing-statement">
              {financingBody}
            </p>
            <div
              className="mt-6 flex items-center gap-3"
              data-testid="footer-operator"
              aria-label={t("footer.operator_label", "Operador de recogida")}
            >
              <img
                src="/brand/client/valoriza-negativo.svg"
                alt="Valoriza Servicios Medioambientales"
                className="h-8 w-auto"
                data-testid="footer-logo-valoriza"
              />
              <div className="border-l border-white/20 pl-3">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">
                  {t("footer.operator_label", "Operador de recogida")}
                </div>
                <div className="text-[13px] text-white/80">
                  Valoriza Servicios Medioambientales
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.24em] text-white/40 mb-4">
              {t("footer.contact_label")}
            </div>
            <div className="flex gap-2 text-sm text-white/70 mb-3">
              <MapPin size={14} className="mt-0.5 shrink-0" />
              <span>{t("footer.address")}</span>
            </div>
            <a
              href="mailto:info@mancomunidadedosalnes.com"
              className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
              data-testid="footer-email"
            >
              <Mail size={14} /> info@mancomunidadedosalnes.com
            </a>
          </div>

          <div className="md:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.24em] text-white/40 mb-4">
              {t("footer.legal_label")}
            </div>
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <Link
                to="/transparencia"
                className="text-white/70 hover:text-white"
                data-testid="footer-transparency"
              >
                {t("nav.transparency")}
              </Link>
              <Link
                to="/politica-privacidade"
                className="text-white/70 hover:text-white"
                data-testid="footer-privacy"
              >
                {t("footer.privacy")}
              </Link>
              <Link
                to="/accesibilidade"
                className="text-white/70 hover:text-white"
                data-testid="footer-accessibility"
              >
                {t("footer.accessibility")}
              </Link>
              <Link
                to="/datos-abertos"
                className="text-white/70 hover:text-white"
                data-testid="footer-opendata"
              >
                {t("nav.opendata")}
              </Link>
              <Link
                to="/documentacion"
                className="text-white/70 hover:text-white"
                data-testid="footer-documentation"
              >
                {t("fase5.doc_title")}
              </Link>
              <Link
                to="/estatisticas"
                className="text-white/70 hover:text-white"
                data-testid="footer-statistics"
              >
                {t("footer.statistics")}
              </Link>
              <Link
                to="/impacto"
                className="text-white/70 hover:text-white"
                data-testid="footer-impacto"
              >
                {t("nav.impacto")}
              </Link>
            </div>
          </div>
        </div>

        <div
          className="mt-14 pt-8 border-t border-white/10"
          data-testid="footer-official-partners"
        >
          <div className="text-[10px] uppercase tracking-[0.24em] text-white/40 mb-5 text-center">
            {t("footer.official_partners", "Financiación e apoio institucional")}
          </div>
          <div className="bg-white/95 rounded-2xl px-6 py-5">
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
              {OFFICIAL_PARTNERS.map((p) => (
                <a
                  key={p.key}
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={p.alt}
                  className="opacity-90 hover:opacity-100 transition-opacity"
                  data-testid={`footer-partner-${p.key}`}
                >
                  <img
                    src={p.src}
                    alt={p.alt}
                    className="h-11 w-auto object-contain"
                    loading="lazy"
                  />
                </a>
              ))}
            </div>
          </div>
          <div className="mt-4 text-[11px] text-white/50 text-center max-w-3xl mx-auto leading-relaxed">
            {t(
              "footer.funding_notice",
              "Proxecto financiado pola Unión Europea — NextGenerationEU, no marco do Plan de Recuperación, Transformación e Resiliencia. Impulsado pola Mancomunidade do Salnés, SEGITTUR e o programa Destinos Turísticos Intelixentes."
            )}
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[11px] uppercase tracking-[0.2em] text-white/40">
          <div>
            {t("footer.copyright", { year })} · {t("footer.rights")}
          </div>
          <div>{t("footer.made")}</div>
        </div>
      </div>
    </footer>
  );
}
